import * as React from "react";
import { Formik } from "formik";
import * as _ from "lodash";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled, { css } from "styled-components";

import { IApiError } from "src/library/utils/errors/apiError";
import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import { CascadeSetting } from "src/library/components/Forms";
import { ButtonLink } from "src/library/components/Link";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import CustomerInfoSidebar from "src/core/customers/components/CustomerInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import useCascadeSettings, {
  cascadeSettingsSchema,
  IFormFieldValues
} from "src/core/shared/components/useCascadeSettings";
import { useInheritedSettingsApi } from "src/core/shared/api/inheritedSettings.api";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { useCustomer } from "src/core/customers/hooks";
import { PageProps } from "src/pages/Router/routes";
import { ERPType } from "src/core/shared/api/globalSettings.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { UpdatedSettingsFields } from "src/library/components/Forms/CascadeSettingField";
import {
  OrderedInheritedSettings,
  IInheritedSetting,
  InheritedSettingNameType,
  InheritedSettingValue
} from "src/core/drops/api/drops.api";

interface UsersPageProps {
  customerId: string;
}

/**
 * Customer settings page
 *
 */
const CustomerOrderSettingsPage: React.ReactFCWithChildren<PageProps<unknown, UsersPageProps>> = props => {
  const { match, terminology } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const { isLoading: loadingCustomer, customer } = useCustomer(customerId);
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const dispatch = useDispatch();
  const [ getSettingsResponse, getSettings ] = useInheritedSettingsApi("GET_CUSTOMER_SETTINGS");
  const [ inheritedValues, setInheritedValues ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const [ overrideValues, setOverrideValues ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const [ requestsCount, setRequestsCount ] = React.useState<number | null>(null);
  const [ errors, setErrors ] = React.useState<IApiError[]>([]);
  const formRef = React.useRef<Formik<IFormFieldValues>>(null);
  const checkPermission = useCheckUserPermission();
  const [ initialValues, setSettings ] = useCascadeSettings(terminology, terminology.customer, customer, inheritedValues, overrideValues);
  const [ settingsToUpdate, setSettingsToUpdate ] = React.useState<UpdatedSettingsFields>([]);
  const [ isSubmitting, setIsSubmitting ] = React.useState(false);

  const canAmend = React.useMemo(() => {
    return checkPermission("amend-global-settings");
  }, [ checkPermission ]);

  React.useEffect(() => {
    getSettings({ customerId });
  }, [ customerId, getSettings ]);

  React.useEffect(() => {
    if (getSettingsResponse.data) {
      const inheritedToSet: OrderedInheritedSettings<IInheritedSetting> = {};
      const overridesToSet: OrderedInheritedSettings<IInheritedSetting> = {};

      _.each(getSettingsResponse.data, useCascadeSettings => {
        const inherited = _.find(useCascadeSettings, setting => setting.scope === "global");
        const override = _.find(useCascadeSettings, setting => setting.scope === "customer");

        if (override) {
          overridesToSet[ override.name ] = override;
        }

        if (inherited) {
          inheritedToSet[ inherited.name ] = inherited;
        }
      });
      setOverrideValues(overridesToSet);
      setInheritedValues(inheritedToSet);
    }

    if (getSettingsResponse.error) {
      dispatch(setGlobalError(getSettingsResponse.error));
    }
  }, [ getSettingsResponse.data ]);

  React.useEffect(() => {
    if (requestsCount === 0) {
      setSettings(null);

      if (errors.length === 0) {
        toast.success("Settings saved!");
      } else {
        if (errors.length === 1) {
          toast.error(`Could not save settings: ${errors[ 0 ].message}. Please try again.`);
        } else {
          toast.error(errors[ 0 ].message);
        }

        setErrors([]);
      }

      getSettings({ customerId });
    }
  }, [
    requestsCount,
    errors,
    getSettings,
    customerId,
    setSettings
  ]);

  // This preps what settings need to be updated and saves them in settingsToUpdate list
  // The list is then handled by each CascadeSetting component which actions the update
  const handleSubmitSettings = (form: IFormFieldValues) => {
    const requests: UpdatedSettingsFields = [];

    _.each(form, setting => {
      if (setting) {
        const initialValue: IInheritedSetting<keyof InheritedSettingValue> | null = (initialValues && initialValues[ setting.name ]) || null;
        const originalOverride = (overrideValues && overrideValues[ setting.name ]) || null;

        if (originalOverride) {
          if (setting.inherited) {
            // original override found
            requests.push({
              params: {
                name: setting.name,
                id: originalOverride.id
              },
              action: "delete"
            });
          } else {
            const params = {
              ...originalOverride,
              value: setting.value
            };

            if (setting.name === "delivery_route") {
              params.deliveryRoute = setting.value;
            }

            // Only push update if value has changed
            if (setting.value !== initialValue?.value) {
              requests.push({
                params,
                action: "update"
              });
            }
          }
        } else {
          // no original override
          if (!setting.inherited) {
            const params: Omit<IInheritedSetting, "id"> = {
              scope: "customer",
              value: setting.value,
              entityId: customerId,
              name: setting.name
            };

            if (setting.name === "delivery_route") {
              params.deliveryRoute = setting.value;
            }

            requests.push({
              params,
              action: "create"
            });
          }
        }
      }
    });
    setRequestsCount(requests.length);
    setSettingsToUpdate(requests);
  };

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const formatErrors = (errors: any, settingName: InheritedSettingNameType) => {
    if (errors) {
      if (errors[ settingName ]) {
        if (typeof errors[ settingName ].value === "object") {
          // TODO: neaten with recursive function
          const result = Object.keys(errors[ settingName ].value).reduce((r, k) => {
            if (typeof errors[ settingName ].value[ k ] === "object") {
              return Object.keys(errors[ settingName ].value[ k ]).reduce((_n, p) => {
                return r.concat(errors[ settingName ].value[ k ][ p ]);
              }, []);
            }

            return r.concat(errors[ settingName ].value[ k ]);
          }, []);

          return `${result}`;
        }

        return `${errors[ settingName ].value}`;
      }
    }

    return "";
  };

  return (
    <Layout
      {...props}
      title={customer ? `${customer.name} - Defaults & Limits` : ""}
    >
      <MainContentWrapper fill>
        {getSettingsResponse.pending || loadingCustomer ? (
          <LoadingSpinner type="circular" />
        ) : (
          <Grid.Bounds direction="vertical">
            {initialValues && (
              <Formik
                ref={formRef}
                validationSchema={cascadeSettingsSchema(terminology)}
                validateOnChange
                validateOnBlur
                initialValues={initialValues}
                onSubmit={handleSubmitSettings}
                render={({
                  handleSubmit, handleBlur, values, setFieldValue, errors
                }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      {_.map(values, (setting, i) => {
                        if (setting) {
                          if (setting.hidden) {
                            return null;
                          }

                          return (
                            <CascadeSettingWrapper
                              id={`${setting.name}-${i}`}
                              key={setting.name}
                            >
                              {setting.Description}

                              {errors[ setting.name ] && (
                                <InlineError>
                                  {formatErrors(errors, setting.name)}
                                </InlineError>
                              )}

                              <CascadeSetting
                                erp={erp as ERPType}
                                name={setting.name}
                                defaultChecked={setting.inherited}
                                disabled={!canAmend}
                                setDefaultChecked={defaultChecked => {
                                  if (defaultChecked) {
                                    setFieldValue(setting.name, {
                                      ...setting,
                                      inherited: true,
                                      value: setting.inheritedValue
                                    });
                                  } else {
                                    setFieldValue(setting.name, {
                                      ...setting,
                                      inherited: false
                                    });
                                  }
                                }}
                                label={setting.label}
                                SettingElement={setting.SettingElement(setFieldValue, setting, handleBlur, !canAmend)}
                                // Props to manage saving updates to settings
                                settingsToUpdate={settingsToUpdate}
                                updateSettingsToUpdate={setSettingsToUpdate}
                                updateIsSubmiting={setIsSubmitting}
                                updateRequestsCount={setRequestsCount}
                                updateErrors={setErrors}
                              />
                            </CascadeSettingWrapper>
                          );
                        }

                        return null;
                      })}
                    </form>
                  );
                }}
              />
            )}
          </Grid.Bounds>
        )}
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerInfoSidebar
          terminology={terminology}
          primaryAction={(
            <ButtonLink
              $buttonType="secondary"
              to="/admin/customers/"
            >
              Cancel
            </ButtonLink>
          )}
          secondaryAction={(
            <Button
              loading={isSubmitting}
              onClick={submitForm}
              disabled={!canAmend}
            >
              Save
            </Button>
          )}
          isLoading={loadingCustomer}
          data={customer}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const InlineError = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.error};
    padding: ${theme.spacing.padding.sm};
    border-radius: ${theme.borderRadius};
  `}
  color: white;
`;

const CascadeSettingWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.padding.lg};
`;

export default CustomerOrderSettingsPage;
